import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle } from "lucide-react";

const STEPS = ["pending", "processing", "shipped", "delivered"];

const OrderView = ({ order }) => (
  <div className="bg-luxa-shell border border-luxa-sand p-8" data-testid="order-detail">
    <div className="flex flex-wrap gap-6 justify-between items-start">
      <div>
        <p className="overline text-luxa-espresso/45">Order</p>
        <p className="font-serif text-3xl mt-1" data-testid="order-id">{order.id}</p>
      </div>
      <div className="text-right">
        <p className="overline text-luxa-espresso/45">Total</p>
        <p className="font-serif text-3xl mt-1">{money(order.total)}</p>
      </div>
    </div>
    <div className="mt-10 flex flex-wrap gap-3">
      {STEPS.map((s, i) => {
        const done = STEPS.indexOf(order.status) >= i;
        return (
          <div key={s} className={`flex-1 min-w-[110px] border-t-2 pt-3 ${done ? "border-luxa-brown" : "border-luxa-espresso/15"}`}>
            <p className={`overline ${done ? "text-luxa-brown" : "text-luxa-espresso/35"}`}>{s}</p>
          </div>
        );
      })}
    </div>
    <div className="mt-9 divide-y divide-luxa-espresso/10">
      {order.items.map((i) => (
        <div key={i.product_id} className="py-4 flex gap-4 items-center">
          <img src={i.image} alt={i.name} className="w-14 h-16 object-cover" />
          <p className="flex-1 font-serif text-lg">{i.name} <span className="text-sm text-luxa-espresso/50">× {i.quantity}</span></p>
          <p className="text-sm">{money(i.line_total)}</p>
        </div>
      ))}
    </div>
    <p className="mt-7 text-sm text-luxa-espresso/60">
      {order.address.full_name}, {order.address.line1} {order.address.line2}, {order.address.city} {order.address.state} {order.address.postal_code}, {order.address.country}
    </p>
    <p className="mt-2 overline text-luxa-espresso/45">
      {order.payment_method === "cod" ? "Cash on delivery" : order.payment_method === "paypal" ? "PayPal" : "Paid by card"} · payment {order.payment_status}
    </p>
    <Link to={`/certificate/${order.id}`} data-testid="certificate-link"
      className="mt-6 inline-flex items-center gap-2 border border-luxa-gold/60 px-5 py-3 overline text-luxa-espresso hover:bg-luxa-espresso hover:text-luxa-cream transition-colors duration-300">
      <span className="font-serif text-luxa-gold">TL</span> View Authenticity Card
    </Link>
  </div>
);

export function OrderConfirmed() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => { api.get(`/orders/track/${orderId}`).then((r) => setOrder(r.data)).catch(() => setOrder(false)); }, [orderId]);
  return (
    <div className="max-w-2xl mx-auto px-5 py-24" data-testid="order-confirmed-page">
      <CheckCircle2 size={38} strokeWidth={1.2} className="text-luxa-brown" />
      <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tighter">Thank you.</h1>
      <p className="mt-5 text-luxa-espresso/65">A confirmation is on its way to your inbox. Keep your order number to track the parcel.</p>
      <div className="mt-10">{order && <OrderView order={order} />}</div>
      <Button asChild className="mt-10 rounded-none bg-luxa-espresso hover:bg-luxa-espresso/85 text-luxa-cream h-12 px-8 overline">
        <Link to="/shop">Continue Shopping</Link>
      </Button>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") { clear(); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { setState("failed"); return; }
      if (++tries > 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else setState("failed");
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="payment-success-page">
      {state === "checking" && <p className="overline text-luxa-espresso/50">Confirming your payment…</p>}
      {state === "paid" && (
        <>
          <CheckCircle2 size={38} strokeWidth={1.2} className="text-luxa-brown mx-auto" />
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tighter">Payment received</h1>
          <p className="mt-5 text-luxa-espresso/65">Order <strong data-testid="paid-order-id">{orderId}</strong> is now in production queue.</p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Button asChild className="rounded-none bg-luxa-brown text-luxa-cream h-12 px-8 overline">
              <Link to={`/track?order=${orderId}`}>Track Order</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-none h-12 px-8 overline">
              <Link to={`/certificate/${orderId}`}>Authenticity Card</Link>
            </Button>
          </div>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle size={38} strokeWidth={1.2} className="text-luxa-brown mx-auto" />
          <h1 className="mt-6 font-serif text-4xl tracking-tighter">We could not confirm your payment</h1>
          <p className="mt-5 text-luxa-espresso/65">If you were charged, your order will appear shortly in your account.</p>
          <Button asChild variant="outline" className="mt-9 rounded-none h-12 px-8 overline"><Link to="/shop">Back to Shop</Link></Button>
        </>
      )}
    </div>
  );
}

export function PaypalReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");

  useEffect(() => {
    let done = false;
    const run = async () => {
      if (done) return;
      done = true;
      try {
        const { data } = await api.post(`/payments/paypal/capture/${orderId}`);
        if (data.payment_status === "paid") { clear(); setState("paid"); } else setState("failed");
      } catch { setState("failed"); }
    };
    run();
    // eslint-disable-next-line
  }, [orderId]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="paypal-return-page">
      {state === "checking" && <p className="overline text-luxa-espresso/50">Confirming your PayPal payment…</p>}
      {state === "paid" && (
        <>
          <CheckCircle2 size={38} strokeWidth={1.2} className="text-luxa-brown mx-auto" />
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tighter">Payment received</h1>
          <p className="mt-5 text-luxa-espresso/65">Order <strong data-testid="paypal-order-id">{orderId}</strong> is confirmed.</p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Button asChild className="rounded-none bg-luxa-brown text-luxa-cream h-12 px-8 overline"><Link to={`/track?order=${orderId}`}>Track Order</Link></Button>
            <Button asChild variant="outline" className="rounded-none h-12 px-8 overline"><Link to={`/certificate/${orderId}`}>Authenticity Card</Link></Button>
          </div>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle size={38} strokeWidth={1.2} className="text-luxa-brown mx-auto" />
          <h1 className="mt-6 font-serif text-4xl tracking-tighter">We could not confirm your PayPal payment</h1>
          <Button asChild variant="outline" className="mt-9 rounded-none h-12 px-8 overline"><Link to="/checkout">Back to Checkout</Link></Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="payment-cancel-page">
      <h1 className="font-serif text-4xl tracking-tighter">Checkout cancelled</h1>
      <p className="mt-5 text-luxa-espresso/65">Your bag is untouched. Pick up where you left off whenever you like.</p>
      <Button asChild className="mt-9 rounded-none bg-luxa-brown text-white h-12 px-8 overline"><Link to="/checkout">Return to Checkout</Link></Button>
    </div>
  );
}

export function TrackOrder() {
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const find = async (e) => {
    e?.preventDefault();
    setError(""); setOrder(null);
    try { setOrder((await api.get(`/orders/track/${id}`)).data); }
    catch { setError("No order found with that number."); }
  };
  useEffect(() => { if (params.get("order")) find(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-3xl mx-auto px-5 py-20" data-testid="track-page">
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter">Track your order</h1>
      <form onSubmit={find} className="mt-9 flex gap-3">
        <Input data-testid="track-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="e.g. TL1A2B3C4D"
          className="rounded-none bg-luxa-shell border-luxa-sand h-12" required />
        <Button data-testid="track-submit" type="submit" className="rounded-none bg-luxa-brown text-white h-12 px-8 overline">Track</Button>
      </form>
      {error && <p className="mt-6 text-sm text-luxa-brown" data-testid="track-error">{error}</p>}
      {order && <div className="mt-10"><OrderView order={order} /></div>}
    </div>
  );
}
